/**
 * DocumentsPage — firm-wide Documents centre. One component serves the three
 * nav variants: All, Recent, Archived (TASK-03).
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Upload, Search, FileText, ShieldCheck, Trash2 } from 'lucide-react'
import { useDocumentStore } from '@/store/documentStore'
import { getDocumentService } from '@/services/documents'
import { toast } from '@/store/toastStore'
import DocumentTable from '@/components/documents/DocumentTable'
import DocumentUploadModal from '@/components/documents/DocumentUploadModal'
import type { Document, DocumentCategory, DocumentStatus } from '@/types/document.types'
import {
  DOCUMENT_CATEGORY_OPTIONS,
  DOCUMENT_STATUS_LABEL,
  labelForCategory,
} from '@/types/document.types'
import { useAuth } from '@/contexts/AuthContext'

type Variant = 'all' | 'recent' | 'archived'

const TITLES: Record<Variant, { title: string; subtitle: string }> = {
  all: { title: 'All Documents', subtitle: 'Every document across your firm' },
  recent: { title: 'Recent Documents', subtitle: 'Most recently uploaded documents' },
  archived: { title: 'Archived Documents', subtitle: 'Documents that have been archived' },
}

const STATUS_OPTIONS: DocumentStatus[] = [
  'pending',
  'uploaded',
  'processing',
  'verified',
  'rejected',
  'archived',
]

export default function DocumentsPage({ variant = 'all' }: { variant?: Variant }) {
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const canUpload = hasPermission('txh.document.upload')
  const canDownload = hasPermission('txh.document.download')
  const canVerify = hasPermission('txh.document.verify')
  const canDelete = hasPermission('txh.document.delete')
  const {
    isLoading,
    filters,
    searchQuery,
    selectedIds,
    loadDocuments,
    setFilters,
    setSearchQuery,
    clearSelection,
    toggleSelectDocument,
    selectAllDocuments,
    deleteDocument,
    verifyDocument,
    uploadDocument,
    bulkDelete,
    bulkVerify,
    getPaginatedDocuments,
  } = useDocumentStore()

  const [showUpload, setShowUpload] = useState(false)

  useEffect(() => {
    loadDocuments()
  }, [loadDocuments])

  // Apply the variant's implicit filter once.
  useEffect(() => {
    if (variant === 'archived') setFilters({ status: 'archived' })
    else setFilters({ status: 'all' })
  }, [variant, setFilters])

  const { items, total } = getPaginatedDocuments()

  const handleDownload = async (doc: Document) => {
    if (!canDownload) return
    try {
      const url = await getDocumentService().getDownloadUrl(doc.id)
      if (url) window.open(url, '_blank', 'noopener')
      else toast.success('Download started', doc.name)
    } catch (err) {
      toast.error('Download failed', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  const handleVerify = async (doc: Document) => {
    if (!canVerify) return
    await verifyDocument(doc.id, true)
    toast.success('Document verified', doc.name)
  }

  const handleDelete = async (doc: Document) => {
    if (!canDelete) return
    const reason = window.prompt(
      `Delete "${doc.name}"? It will be inaccessible but recoverable for 30 days. Enter a reason:`
    )?.trim()
    if (!reason) return
    await deleteDocument(doc.id, reason)
    toast.success('Document moved to Recycle Bin', doc.name)
  }

  const { title, subtitle } = TITLES[variant]

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">{title}</h1>
          <p className="text-text-secondary mt-1">{subtitle}</p>
        </div>
        {canUpload && (
          <button type="button" onClick={() => setShowUpload(true)} className="btn btn-primary gap-2">
            <Upload className="w-4 h-4" />
            Upload Document
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card card-elevated p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search documents by name, description, or category…"
            className="input input-bordered w-full pl-10"
          />
        </div>
        <select
          value={filters.category ?? 'all'}
          onChange={(e) => setFilters({ category: e.target.value as DocumentCategory | 'all' })}
          className="select select-bordered"
          aria-label="Filter by category"
        >
          <option value="all">All categories</option>
          {DOCUMENT_CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {labelForCategory(c)}
            </option>
          ))}
        </select>
        {variant !== 'archived' && (
          <select
            value={filters.status ?? 'all'}
            onChange={(e) => setFilters({ status: e.target.value as DocumentStatus | 'all' })}
            className="select select-bordered"
            aria-label="Filter by status"
          >
            <option value="all">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {DOCUMENT_STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Bulk actions */}
      {selectedIds.length > 0 && (canVerify || canDelete) && (
        <div className="card card-elevated p-3 flex items-center justify-between">
          <span className="text-sm text-text-primary flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {selectedIds.length} selected
          </span>
          <div className="flex gap-2">
            {canVerify && (
              <button
                type="button"
                onClick={async () => {
                  if (!canVerify) return
                  await bulkVerify(selectedIds)
                  toast.success('Documents verified', `${selectedIds.length} updated`)
                }}
                className="btn btn-outline btn-sm gap-1"
              >
                <ShieldCheck className="w-4 h-4" /> Verify
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={async () => {
                  if (!canDelete) return
                  if (!window.confirm(`Delete ${selectedIds.length} documents?`)) return
                  await bulkDelete(selectedIds)
                  toast.success('Documents deleted')
                }}
                className="btn btn-error btn-sm gap-1"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            )}
            <button type="button" onClick={clearSelection} className="btn btn-ghost btn-sm">
              Clear
            </button>
          </div>
        </div>
      )}

      <p className="text-sm text-text-secondary">{total} document{total !== 1 ? 's' : ''}</p>

      <DocumentTable
        documents={items}
        isLoading={isLoading}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelectDocument}
        onSelectAll={selectAllDocuments}
        onView={(d) => navigate(`/documents/${d.id}`)}
        onDownload={canDownload ? handleDownload : undefined}
        onVerify={canVerify ? handleVerify : undefined}
        onDelete={canDelete ? handleDelete : undefined}
      />

      {canUpload && (
        <DocumentUploadModal
          isOpen={showUpload}
          onClose={() => setShowUpload(false)}
          onSubmit={async (input) => {
            if (!canUpload) return
            const doc = await uploadDocument(input)
            if (doc) {
              toast.success('Document uploaded', doc.name)
              setShowUpload(false)
              await loadDocuments()
            }
          }}
        />
      )}
    </div>
  )
}
